import {
  Anchor,
  Content,
  type PopoverContentProps,
  Portal,
  Root,
} from "@radix-ui/react-popover"
import type { ReactNode } from "react"

import { theme } from "../../theme"
import styles from "./Popover.module.css"

export interface PopoverProps {
  anchor: ReactNode
  children?: ReactNode
  focusOnEnter?: boolean
  open?: boolean
  openDelay?: number
  closeDelay?: number
  align?: "start" | "center" | "end"
  side?: "top" | "right" | "bottom" | "left"
  sideOffset?: number
  portalContainer?: HTMLElement
  onOpenChange?: (open: boolean) => void
  onInteractOutside?: (event: PopoverInteractOutsideEvent) => void
  onEscapeKeyDown?: (event: KeyboardEvent) => void
}

export type PopoverInteractOutsideEvent = Parameters<
  NonNullable<PopoverContentProps["onInteractOutside"]>
>[0]

export function Popover(props: PopoverProps) {
  const {
    align,
    focusOnEnter = true,
    side,
    sideOffset,
    anchor,
    children,
    open,
    openDelay = 0,
    closeDelay = 0,
    portalContainer,
    onOpenChange,
    onInteractOutside,
    onEscapeKeyDown,
  } = props

  function onOpenAutoFocus(evt: Event) {
    if (!focusOnEnter) evt.preventDefault()
  }

  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Anchor asChild>{anchor}</Anchor>
      <Portal container={portalContainer}>
        <Content
          className={styles.cardContent}
          style={{
            "--Popover-open-delay": `${openDelay}ms`,
            "--Popover-close-delay": `${closeDelay}ms`,
            // Needed to allow interaction when a Dialog is open.
            // Radix disables other Radix-controlled components when a Dialog is open.
            pointerEvents: "auto",
          }}
          align={align}
          collisionPadding={parseFloat(theme.space[16])}
          side={side}
          sideOffset={sideOffset}
          onOpenAutoFocus={onOpenAutoFocus}
          onInteractOutside={onInteractOutside}
          onEscapeKeyDown={onEscapeKeyDown}
          /* Scroll event propagation is stopped to fix scroll inside dialogs
          https://github.com/prismicio/editor/pull/1164
          https://github.com/radix-ui/primitives/issues/1159 */
          onWheel={(event) => event.stopPropagation()}
        >
          {children}
        </Content>
      </Portal>
    </Root>
  )
}
